/**
 * All walkthrough / discovery / popup copy for the Interactive SOAP Sandbox
 * (TTFV Story 5), verbatim from the CJ > Dev Story board spec. Centralized so
 * copy edits never require touching component logic, and so a future
 * admin-editable override layer (like TRIAL_TOUR_MANIFESTS) has one source.
 */

/** Pill shown near the top of every pop-up that demonstrates EHR
 *  functionality that isn't interactive in the demo (tester note #2,
 *  2026-09-08; supersedes the spec's ✨ Preview Mode badge). */
export const FULL_EHR_FEATURE_PILL = {
  label: 'FULL EHR FEATURE',
  tooltip: 'This feature is available in the full ChiroUp EHR but is not interactive in this demo.',
};

/** The four-step progress indicator, always visible. */
export const SANDBOX_STEPS = [
  'Review Subjective',
  'Select Diagnosis',
  'Finalize OAP',
  'Review & Sign',
] as const;

export const SUBJECTIVE_COPY = {
  stepLabel: 'STEP 1 OF 5 · REVIEW SUBJECTIVE',
  title: 'Your Subjective = DONE.',
  body: [
    'Before the visit, ChiroUp’s Chief Complaint Survey acts like a virtual intern, asking relevant follow-up questions and building your Subjective, so you spend less time gathering information and more time with your patient.',
    'Review what’s here, change anything you need, then move on.',
  ],
  cta: 'Next: Assessment',
};

export const SUBJECTIVE_EDIT_POPUP = {
  title: 'Need to make a change? Easy.',
  body: 'Edit any response directly, or use Voice-to-Chart to capture updates as you go. Your note. Your call.',
};

/** Verbiage from tester note #9 (2026-09-08). */
/** "How is this already filled out?" three-step visual (Kara, 2026-09-28). */
export const HOW_COLLECTED_FLYOUT = {
  /** Rendered as the step-1 callout's secondary link; the arrow is part of
   *  the string, matching OAP_SKIP_TO_PLAN. */
  trigger: 'How is this already filled out? →',
  title: 'How is this already filled out?',
  subtitle:
    'As soon as an appointment is booked, ChiroUp automatically sends your patient the Chief Complaint Survey.',
  steps: [
    {
      title: 'Appointment is booked',
      caption: 'ChiroUp automatically sends the Chief Complaint Survey.',
    },
    {
      title: 'Chief Complaint Survey is completed',
      caption: 'Questions adapt to gather the information needed for your Subjective.',
    },
    {
      title: 'Patient checks in and your Subjective is ready',
      caption: 'Survey responses automatically complete your Subjective.',
    },
  ],
  /** Illustration labels inside the three step cards. */
  appointmentCard: { title: 'New Appointment', chip: 'Chief Complaint Survey sent to patient' },
  surveyCard: {
    question: 'Where is your pain located?',
    options: ['Lower back', 'Neck', 'Shoulder'],
  },
  subjectiveCard: {
    title: 'Subjective',
    chip: 'Automatically filled',
    rows: [
      'Chief Complaint',
      'History of Present Illness',
      'Aggravating Factors',
      'Relieving Factors',
    ],
  },
  bannerLead: 'Less time gathering information. More time with your patient.',
  bannerBody:
    'Before your patient even steps into the treatment room, their Subjective is already waiting for you.',
  cta: 'Got it!',
};

export const VOICE_TO_CHART_COPY = {
  /** Kara / Dr. B + Green Team (2026-09-22): the demonstration this button
   *  opens is titled "AI Voice to Chart", unhyphenated, as quoted. */
  title: 'AI Voice to Chart',
  optionalBadge: 'OPTIONAL',
  headline: 'One click. Just talk. Your Subjective is done.',
  body: 'Voice-to-Chart listens to your patient conversation and automatically adds the clinically relevant details to your Subjective.',
  conversationLabel: 'Patient conversation',
  conversationTag: '(abbreviated)',
  /** Abbreviated sample dialogue per the tester-provided design (2026-09-08). */
  transcript: [
    { speaker: 'Doctor', line: 'What have you tried at home?' },
    { speaker: 'Anita', line: 'Ice and ibuprofen. They help a little.' },
    { speaker: 'Doctor', line: 'Any numbness or tingling down your legs?' },
    { speaker: 'Anita', line: 'No, none of that.' },
    { speaker: 'Doctor', line: "How's your sleep?" },
    { speaker: 'Anita', line: 'It wakes me up sometimes when I roll over.' },
    { speaker: 'Doctor', line: 'Any prior back issues?' },
    { speaker: 'Anita', line: 'No, this is the first time.' },
  ],
  conversationContinues: '... (conversation continues) ...',
  subjectiveLabel: 'Subjective',
  subjectiveTag: '(Automatically Generated)',
  subjectiveText:
    'The patient reports low back pain that started approximately 2 weeks ago while bending down. The pain is aggravated by prolonged sitting and forward bending. She rates the pain as a 6/10, described as a dull ache with intermittent sharpness. She has been using ice and over-the-counter ibuprofen with mild relief. The patient denies weakness, numbness, or tingling in her lower extremities. Sleep is occasionally disrupted when rolling over. She reports no prior episodes of low back pain.',
  aiCallout: 'AI captures what matters.',
  aiCalloutDetail: "It pulls out the clinically relevant details and leaves out what doesn't.",
  cta: 'Got it, back to Subjective',
};

/** Restructured to the tester-provided graphic (note #10, 2026-09-08):
 *  clinical-workflow icon row, diagnosis banner, and the S→A→O→P strip. */
export const SAOP_MODAL = {
  titlePre: 'Why ',
  titleEmphasis: 'SAOP',
  titlePost: ' instead of SOAP?',
  subtitle:
    'SOAP was a logical way to learn how to document, but it\u2019s not the order that you practice now.',
  workflow: [
    'You review the patient\u2019s story.',
    'Examine them.',
    'Treat them.',
    'Then document the visit.',
  ],
  banner: 'By the time you take the note, you already know the diagnosis.',
  leveragePre: 'So why not ',
  leverageEmphasis: 'leverage',
  leveragePost: ' your diagnosis?',
  letterLabel: ['Assessment', 'Diagnosis'],
  wizardLead: 'ChiroUp\u2019s patent-pending SAOP Wizard',
  wizardBody:
    ' uses your diagnosis to automatically populate the most likely exam findings, orthopedic tests, and treatment options.',
  builtBy: 'Built by chiropractors.',
  cta: 'Got it. Show me SAOP \u2192',
};

export const DIAGNOSIS_COPY = {
  stepLabel: 'STEP 2 OF 5 · SELECT DIAGNOSIS',
  title: 'Make the clinical call.',
  body: [
    'Choose the diagnosis or diagnoses that best fit this patient.',
    'Then watch ChiroUp build from your decisions, pulling in the most relevant evaluation and treatment details from your customizable ChiroUp protocols.',
  ],
  cta: 'Done →',
  ctaDisabledHint: 'Select a diagnosis to continue.',
  narrowedNote:
    "We've narrowed the choices for this walkthrough.\nIn the full ChiroUp EHR, you can select multiple diagnoses from any region.",
};

/**
 * Shown when the prospect attempts to exceed the sandbox diagnosis cap. Spec
 * body text says "maximum of three" but the popup copy says two — the popup
 * copy is treated as canonical pending CJ confirmation (see DIAGNOSIS_CAP).
 */
export const DIAGNOSIS_CAP_POPUP = {
  title: 'Keeping it simple for the demo.',
  body: 'This walkthrough is limited to two lumbar diagnoses. In the full ChiroUp EHR, you can select as many diagnoses as needed across multiple regions.',
  cta: 'Got it',
};

export const DIAGNOSIS_CAP = 2;

/** Escape hatch on every Objective walkthrough box (tester note #15): users
 *  can roam the modules in any order, so someone who finishes early must not
 *  be stranded outside Orthopedic, the only beat whose Done reaches Plan. */
export const OAP_SKIP_TO_PLAN = 'Done with Objective findings? Skip to Plan →';

/** Back control on every walkthrough callout after the first step
 *  (requirement 2026-09-28): returns to the immediately preceding step with
 *  all entered work retained. */
export const WALKTHROUGH_BACK = '← Back';

/** OAP walkthrough callouts. Step numbering follows the five-step model
 *  (Kara, 2026-09-28): every O module shows a static STEP 3 OF 5, Plan is
 *  STEP 4 OF 5, Review & Sign is STEP 5 OF 5, and each CTA names the step
 *  it advances to. */
export const OAP_COPY = {
  joints: {
    stepLabel: 'STEP 3 OF 5 · JOINTS',
    title: 'Select the involved segments.',
    body: 'Select the involved spinal segments. Your selections flow directly into the note and help shape what comes next.',
    cta: 'Next: Range of Motion',
  },
  rangeOfMotion: {
    stepLabel: 'STEP 3 OF 5 · RANGE OF MOTION',
    title: "Set the patient's range of motion.",
    body: 'Make a global adjustment or fine-tune any movement individually.',
    cta: 'Next: Myofascial',
  },
  myofascial: {
    stepLabel: 'STEP 3 OF 5 · MYOFASCIAL',
    title: 'Your diagnosis just did the work.',
    body: 'Your assessment automatically pulled in the most likely myofascial findings from ChiroUp protocols.\nInstead of documenting each muscle individually, review the recommendations, accept them with one click, and then edit anything as desired.',
    footnote: 'Fully customizable by diagnosis.',
    cta: 'Next: Orthopedic',
  },
  orthopedic: {
    stepLabel: 'STEP 3 OF 5 · ORTHOPEDIC',
    title: 'Your diagnosis keeps doing the work.',
    body: 'ChiroUp automatically recommends the most likely orthopedic tests and exam findings from our customizable, evidence-based protocols.\nChiroUp makes the recommendations. You make the call. Accept all suggested findings, then edit only what needs to change.',
    cta: 'Next: Plan',
  },
  /** One combined Plan beat (round-2 tester note #5: the two step-5 beats
   *  played the same step label twice). */
  plan: {
    stepLabel: 'STEP 4 OF 5 · PLAN',
    title: 'Build your Plan. Build your bill.',
    body: 'As you add services to the Plan, ChiroUp is building the bill right alongside it.\nReview, add, or modify the charges, units, and details, then import them directly into your bill. No separate workflow. No documenting it twice.',
    cta: 'Review & Sign',
  },
  reviewSign: {
    stepLabel: 'STEP 5 OF 5 · REVIEW & SIGN',
    title: 'Your note is built.',
    body: 'SAOP connects your clinical decisions into a complete note, built the way you practice.\nReview. Refine. Sign.',
    cta: 'Sign Note →',
  },
};

/** Discovery pop-ups (dismissible, informational only). */
export const DISCOVERY_COPY = {
  jointsOtherRegions: {
    title: 'Document any region you need.',
    body: "In the full ChiroUp EHR, you can quickly document joint findings throughout the body. We've narrowed this walkthrough to the region relevant to this sample patient.",
  },
  romOtherRegions: {
    title: 'Range of motion wherever you need it.',
    body: 'The full ChiroUp EHR supports range-of-motion documentation across all applicable regions. This walkthrough stays focused on the region relevant to this patient.',
  },
  addMuscles: {
    title: 'Missing a muscle? Add it.',
    body: 'ChiroUp starts with the muscles most likely for this diagnosis. Need another? Simply select it and keep moving.\nAnd yes, you can customize your defaults anytime, so ChiroUp starts with your preferences next time.',
  },
  /** Tester note #19: the "+" details control on an orthopedic test. */
  orthoDetails: {
    title: 'Add as much detail as you need.',
    body: 'In the full ChiroUp EHR, you can add additional details to any test using customizable Quick Picks and preset macros, making your documentation as simple or detailed as you want.',
  },
  orthoTutorial: {
    title: 'Your clinical GPS is built right in.',
    body: 'ChiroUp includes step-by-step tutorial videos and supporting clinical resources, so guidance is available whenever you need it.',
    cta: 'Watch this example ▶',
  },
  addTests: {
    title: 'Any test you need.',
    body: 'ChiroUp starts with the tests most relevant to your diagnosis. Add or remove anything based on what you performed.\nHave a go-to test we didn’t recommend? Set it as a default for that condition, and ChiroUp will automatically include it every time.',
  },
  neurologic: {
    title: 'Neurologic documentation, made easy.',
    body: 'In the EHR, you’ll be able to use Quick Picks to document normal findings in a single click, or select Abnormal and add exactly what you found in just a couple more.\nYou stay in control of what gets documented.',
  },
  imagingAndTests: {
    title: 'Never lose track of an order.',
    body: 'Order and track imaging and tests directly from the patient’s Plan, whether they’re performed in-house or outside.\nChiroUp keeps everything connected, from ordered tests to completed results. And when services are performed in-house, they can flow directly into the bill.',
  },
  treatmentDetails: {
    title: 'Make your note as detailed as you want.',
    body: 'The EHR offers customizable Quick Picks to document exactly what you did, such as manipulation techniques, treatment locations, or other service-specific details, in just a few clicks.',
  },
  modalities: {
    title: 'Prescribe it once. Document it with ease.',
    body: 'Choose the modality you performed or prescribed. In the full EHR, you or your team can document the applicable time, levels, units, and other details at each visit.\nThose details can flow directly into the note and, when appropriate, into billing.\nOne workflow. Connected documentation and billing.',
  },
  /** Tester note #21. The provided title used em-dashes ("Recommend—and
   *  document—..."); swept to parentheses per the no-em-dash verbiage rule. */
  supplements: {
    title: 'Recommend (and document) supplements and supplies right here.',
    body: 'Associate your favorite supplements and supplies with any condition, or prescribe them on the fly. If you provide them in-office, ChiroUp lets you document and bill for them in one click.\nLess clicking. Everything documented.',
  },
  rehab: {
    title: 'Turn your diagnosis into a rehab plan.',
    body: 'ChiroUp uses your diagnosis to recommend the most appropriate exercises and builds a personalized rehab program in seconds for you to review and customize as desired.\nSend it directly to your patient, complete with videos, instructions, and progress tracking.\nLess time building plans. More time helping patients recover.',
  },
  noteFormatting: {
    title: 'Your note. Your way.',
    body: 'In the full EHR, you can customize every detail, from your preferred sections and Quick Picks to defaults that make documentation work the way you practice.',
  },
};

export const DISCOVERY_DISMISS = 'Got it';

/** Post-sign walkthrough callout beside the signed note (round-2 tester note
 *  #6: was a blocking pop-up with a "Review the note" button — the note now
 *  just stays visible with this callout carrying the tour forward). */
export const COMPLETED_NOTE_CALLOUT = {
  title: 'You just built your first SAOP note. 🎉',
  body: 'A new way to document, built around the way you practice.',
  cta: 'Continue →',
};

/** Charting Completion screen, to the provided design (Kara, 2026-09-28):
 *  confetti header, celebration title, three icon cards, time-savings
 *  banner, and Back to home as the single final action. */
export const COMPLETION_SCREEN = {
  titlePre: "That's it. Your note is ",
  titleEmphasis: 'done!',
  /** The provided design joins the clauses with an em-dash ("minutes—so");
   *  swept to a comma per the no-em-dash verbiage rule. */
  subtitle:
    'From subjective to plan, ChiroUp builds a complete, customized note in minutes, so you can focus on what matters most: your patients.',
  cards: [
    {
      title: 'Your documentation',
      body: 'Customizable Quick Picks, sections, defaults, and wording keep you in control.',
    },
    {
      title: 'Clinical tools built in',
      body: 'Condition reports, exercise plans, and PCP reports are already connected to the patient and diagnosis.',
    },
    {
      title: 'Follow-ups are even faster',
      body: 'Prior findings carry forward for confirmation or change, so progress is easy to document without creating a cloned note.',
    },
  ],
  bannerLead: 'More than 80% of providers save 1–3 minutes per note',
  bannerBody: 'with the SAOP Wizard and Voice-to-Chart.',
  cta: 'Back to home',
  footer: 'Keep exploring ChiroUp and see what else can help your practice run smoother.',
};
